/**
 * Vanilla JS Initializers
 * ビルドモードでプリレンダリングされたインタラクティブコンポーネントを初期化
 * dev モードでは data-* 要素が存在しないため何もしない
 *
 * セキュリティ注記: innerHTML はビルド時に安全にシリアライズされた
 * コンテンツの再構築にのみ使用される（ユーザー入力は含まれない）
 */
// ====================================================================
// 入力モダリティ検知（スキップリンク表示制御）
// ====================================================================
// スクリプト（Cookieバナー・UserWay 等）からの focus() 呼び出しでも
// :focus-visible が発火するブラウザヒューリスティックがあるため、
// 「ユーザーが実際に Tab キーを押したか」を検知して <html> に
// .is-user-tabbing を付与する。マウス・タッチ操作で解除。
// CSS 側（_custom.scss）は html.is-user-tabbing .skip-link:focus で表示する。
// DOMContentLoaded を待たず即時にリスナーを登録する。
document.addEventListener('keydown', (e) => {
  if (e.key === 'Tab') document.documentElement.classList.add('is-user-tabbing');
}, true);
document.addEventListener('mousedown', () => {
  document.documentElement.classList.remove('is-user-tabbing');
}, true);
document.addEventListener('touchstart', () => {
  document.documentElement.classList.remove('is-user-tabbing');
}, { capture: true, passive: true });

document.addEventListener('DOMContentLoaded', () => {
  initCarousels();
  initTabContainers();
  initModals();
  initNewsStories();
  initIrRelatedCards();
  initNewsLists();
  initCodeBlockCopy();
  initScrollHint();
});

// ====================================================================
// Carousel
// ====================================================================
function initCarousels() {
  document.querySelectorAll('[data-carousel]').forEach(el => {
    const config = JSON.parse(el.dataset.carousel);
    const track = el.querySelector('.mod-carousel-track');
    const slides = el.querySelectorAll('.mod-carousel-slide');
    const dotsWrap = el.querySelector('.mod-carousel-dots');
    const stopBtn = el.querySelector('.mod-carousel-stop-btn');
    const prevBtn = el.querySelector('.mod-carousel-prev');
    const nextBtn = el.querySelector('.mod-carousel-next');
    if (!track || slides.length === 0) return;

    const isKv = config.variant === 'kv';

    // ドットをスライド数に合わせて動的生成（停止ボタンの前に挿入）
    if (dotsWrap) {
      dotsWrap.querySelectorAll('.mod-carousel-dot').forEach(d => d.remove());
      for (let i = 0; i < slides.length; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mod-carousel-dot' + (isKv ? ' _kv-dot' : '') + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', `スライド ${i + 1}`);
        if (isKv) {
          const prog = document.createElement('span');
          prog.className = 'mod-carousel-dot__progress';
          prog.style.width = '0%';
          dot.appendChild(prog);
        }
        dotsWrap.insertBefore(dot, stopBtn || null);
      }
    }
    const dots = el.querySelectorAll('.mod-carousel-dot');
    let currentIndex = 0;
    let autoplay = config.autoplay;
    let interval = config.interval || 5000;
    let timer = null;
    let progressTimer = null;
    let progress = 0;

    function goTo(index) {
      currentIndex = index;
      track.style.transform = `translateX(-${currentIndex * 100}%)`;
      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === currentIndex);
        const prog = d.querySelector('.mod-carousel-dot__progress');
        if (prog) prog.style.width = '0%';
      });
      progress = 0;
      resetTimer();
    }

    function next() {
      goTo(currentIndex < slides.length - 1 ? currentIndex + 1 : 0);
    }

    function prev() {
      goTo(currentIndex > 0 ? currentIndex - 1 : slides.length - 1);
    }

    function startTimer(resume) {
      if (!autoplay || slides.length <= 1) return;
      if (!resume) progress = 0;
      if (isKv) {
        const step = 50;
        progressTimer = setInterval(() => {
          progress += (step / interval) * 100;
          const activeDot = el.querySelector('.mod-carousel-dot.is-active .mod-carousel-dot__progress');
          if (activeDot) activeDot.style.width = progress + '%';
          if (progress >= 100) { progress = 0; next(); }
        }, step);
      } else {
        const remaining = resume ? interval * (1 - progress / 100) || interval : interval;
        timer = setTimeout(function tick() {
          next();
          timer = setInterval(next, interval);
        }, remaining);
      }
    }

    function resetTimer() {
      if (timer) { clearInterval(timer); clearTimeout(timer); }
      if (progressTimer) clearInterval(progressTimer);
      timer = null;
      progressTimer = null;
      startTimer();
    }

    if (prevBtn) prevBtn.addEventListener('click', prev);
    if (nextBtn) nextBtn.addEventListener('click', next);
    dots.forEach((d, i) => d.addEventListener('click', () => goTo(i)));

    if (stopBtn) {
      stopBtn.addEventListener('click', () => {
        if (autoplay) {
          autoplay = false;
          if (timer) { clearInterval(timer); clearTimeout(timer); }
          if (progressTimer) clearInterval(progressTimer);
          timer = null;
          progressTimer = null;
        } else {
          autoplay = true;
          startTimer(true);
        }
        const svg = stopBtn.querySelector('svg use');
        if (svg) {
          const href = svg.getAttribute('xlink:href') || '';
          const base = href.substring(0, href.lastIndexOf('#'));
          svg.setAttribute('xlink:href', base + '#' + (autoplay ? 'stop' : 'play'));
        }
        stopBtn.setAttribute('aria-label', autoplay ? '自動再生停止' : '自動再生開始');
      });
    }

    startTimer();
  });
}

// ====================================================================
// Tab Container
// ====================================================================
function initTabContainers() {
  document.querySelectorAll('[data-tab-container]').forEach(el => {
    const buttons = el.querySelectorAll('[role="tab"]');
    const panels = el.querySelectorAll('[role="tabpanel"]');
    if (buttons.length === 0) return;

    function switchTab(index) {
      buttons.forEach((btn, i) => {
        const active = i === index;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', String(active));
        btn.setAttribute('tabindex', active ? '0' : '-1');
      });
      panels.forEach((panel, i) => {
        const active = i === index;
        panel.classList.toggle('is-active', active);
        panel.classList.toggle('is-visible', active);
      });
      el.dispatchEvent(new CustomEvent('tab-change', { bubbles: true, detail: { index } }));
    }

    buttons.forEach((btn, i) => {
      btn.addEventListener('click', () => switchTab(i));
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); switchTab(i > 0 ? i - 1 : buttons.length - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); switchTab(i < buttons.length - 1 ? i + 1 : 0); }
      });
    });
  });
}

// ====================================================================
// Modal
// ====================================================================
function initModals() {
  document.querySelectorAll('[data-modal]').forEach(el => {
    const config = JSON.parse(el.dataset.modal);
    const innerContent = el.cloneNode(true).childNodes;

    function openModal() {
      const backdrop = document.createElement('div');
      backdrop.className = 'mod-modal-backdrop';
      backdrop.setAttribute('role', 'dialog');
      backdrop.setAttribute('aria-modal', 'true');

      const modal = document.createElement('div');
      modal.className = 'mod-modal';

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'mod-modal-close';
      closeBtn.setAttribute('aria-label', '閉じる');
      closeBtn.textContent = '\u00D7';

      const content = document.createElement('div');
      if (config.type === 'youtube') {
        modal.classList.add('mod-modal--youtube');
        content.className = 'mod-modal-content mod-modal-content--youtube';
        if (config.src) {
          const wrap = document.createElement('div');
          wrap.className = 'mod-modal-youtube';
          const iframe = document.createElement('iframe');
          iframe.src = config.src;
          iframe.title = 'YouTube video';
          iframe.frameBorder = '0';
          iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
          iframe.allowFullscreen = true;
          wrap.appendChild(iframe);
          content.appendChild(wrap);
        }
      } else {
        content.className = 'mod-modal-content';
        if (config.src) {
          const img = document.createElement('img');
          img.src = config.src;
          img.alt = config.alt || '';
          img.className = 'mod-modal-img';
          content.appendChild(img);
        }
        for (const node of el.childNodes) content.appendChild(node.cloneNode(true));
      }

      modal.appendChild(closeBtn);
      modal.appendChild(content);
      backdrop.appendChild(modal);

      function close() { backdrop.remove(); document.removeEventListener('keydown', escHandler); }
      function escHandler(e) { if (e.key === 'Escape') close(); }

      backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
      closeBtn.addEventListener('click', close);
      document.addEventListener('keydown', escHandler);
      document.body.appendChild(backdrop);
    }

    if (config.trigger) {
      const triggerEl = document.querySelector(config.trigger);
      if (triggerEl) triggerEl.addEventListener('click', openModal);
    }
  });
}

// ====================================================================
// News Stories (レスポンシブ: デスクトップ=グリッド, モバイル=カルーセル)
// ====================================================================
function initNewsStories() {
  document.querySelectorAll('[data-news-stories]').forEach(el => {
    const items = JSON.parse(el.dataset.newsStories);
    if (items.length <= 1) return;
    let isMobile = false;
    let currentIndex = 0;
    const slidesPerView = 2;

    function createCard(item, truncateTitle) {
      const title = truncateTitle && item.title && item.title.length > 19
        ? item.title.substring(0, 19) + '...' : (item.title || '');
      const a = document.createElement('a');
      a.href = item.href || '#';
      a.className = 'top-stories-section__cards__card rt_bn_news_stories__card';
      if (item.blank) { a.target = '_blank'; a.rel = 'noopener noreferrer'; }

      const img = document.createElement('img');
      img.src = item.thumbnail || '';
      img.alt = item.alt || '';
      img.loading = 'lazy';
      img.className = 'top-stories-section__cards__img rt_bn_news_stories__img';

      const overlay = document.createElement('div');
      overlay.className = 'top-stories-section__cards__overlay rt_bn_news_stories__overlay';

      const pTitle = document.createElement('p');
      pTitle.className = 'top-stories-section__cards__title rt_bn_news_stories__title';
      const span = document.createElement('span');
      span.textContent = title;
      pTitle.appendChild(span);

      const pMore = document.createElement('p');
      pMore.className = 'top-stories-section__cards__more';
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/common/svg/sprite.svg#arrow_right_circle');
      svg.appendChild(use);
      const moreSpan = document.createElement('span');
      moreSpan.textContent = 'Read More';
      pMore.appendChild(svg);
      pMore.appendChild(moreSpan);

      overlay.appendChild(pTitle);
      overlay.appendChild(pMore);
      a.appendChild(img);
      a.appendChild(overlay);
      return a;
    }

    function buildCarousel() {
      const wrapper = document.createElement('div');
      wrapper.className = 'top-stories-section__carousel rt_bn_news_stories';
      const track = document.createElement('div');
      track.className = 'top-stories-section__carousel__track';
      for (const item of items) {
        const slide = document.createElement('div');
        slide.className = 'top-stories-section__carousel__slide';
        slide.appendChild(createCard(item, true));
        track.appendChild(slide);
      }
      wrapper.appendChild(track);

      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'top-stories-section__carousel__prev';
      prevBtn.setAttribute('aria-label', '前へ');
      prevBtn.style.display = 'none';
      const prevSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      prevSvg.setAttribute('viewBox', '0 0 24 24');
      const prevUse = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      prevUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/common/svg/sprite.svg#arrow_left');
      prevSvg.appendChild(prevUse);
      prevBtn.appendChild(prevSvg);

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'top-stories-section__carousel__next';
      nextBtn.setAttribute('aria-label', '次へ');
      const nextSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      nextSvg.setAttribute('viewBox', '0 0 24 24');
      const nextUse = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      nextUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/common/svg/sprite.svg#arrow_right');
      nextSvg.appendChild(nextUse);
      nextBtn.appendChild(nextSvg);

      wrapper.appendChild(prevBtn);
      wrapper.appendChild(nextBtn);

      currentIndex = 0;
      function update() {
        track.style.transform = `translateX(-${currentIndex * 50}%)`;
        prevBtn.style.display = currentIndex > 0 ? '' : 'none';
        nextBtn.style.display = currentIndex < items.length - slidesPerView ? '' : 'none';
      }
      prevBtn.addEventListener('click', () => { if (currentIndex > 0) { currentIndex--; update(); } });
      nextBtn.addEventListener('click', () => { if (currentIndex < items.length - slidesPerView) { currentIndex++; update(); } });
      update();

      return wrapper;
    }

    function buildGrid() {
      const frag = document.createDocumentFragment();
      for (const item of items) frag.appendChild(createCard(item, false));
      return frag;
    }

    function checkResize() {
      const nowMobile = window.innerWidth <= 1024;
      if (nowMobile === isMobile) return;
      isMobile = nowMobile;
      while (el.firstChild) el.removeChild(el.firstChild);
      if (isMobile) {
        el.className = '';
        el.appendChild(buildCarousel());
      } else {
        el.className = 'top-stories-section__cards rt_bn_news_stories';
        el.appendChild(buildGrid());
      }
    }

    window.addEventListener('resize', checkResize);
    checkResize();
  });
}

// ====================================================================
// IR Related Cards (レスポンシブ: デスクトップ=グリッド, モバイル=カルーセル)
// ====================================================================
function initIrRelatedCards() {
  document.querySelectorAll('[data-ir-cards]').forEach(el => {
    const items = JSON.parse(el.dataset.irCards);
    if (items.length <= 1) return;
    let isMobile = false;
    let currentIndex = 0;
    const slidesPerView = 2;

    function createCard(item) {
      const a = document.createElement('a');
      a.href = item.href || '#';
      a.className = 'ir-content-card';
      if (item.blank) { a.target = '_blank'; a.rel = 'noopener noreferrer'; }

      const imgWrap = document.createElement('div');
      imgWrap.className = 'ir-content-card__img';
      const img = document.createElement('img');
      img.src = item.image || '';
      img.alt = item.alt || '';
      imgWrap.appendChild(img);

      const titleWrap = document.createElement('div');
      titleWrap.className = 'ir-content-card__title';
      const inner = document.createElement('span');
      inner.className = 'ir-content-card__title-inner';
      const iconSpan = document.createElement('span');
      iconSpan.className = 'icon-md';
      iconSpan.setAttribute('aria-hidden', 'true');
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/common/svg/sprite.svg#arrow_right_circle');
      svg.appendChild(use);
      iconSpan.appendChild(svg);
      const textSpan = document.createElement('span');
      textSpan.className = '_text';
      textSpan.textContent = item.title || '';
      inner.appendChild(iconSpan);
      inner.appendChild(textSpan);
      titleWrap.appendChild(inner);

      a.appendChild(imgWrap);
      a.appendChild(titleWrap);
      return a;
    }

    function buildCarousel() {
      const wrapper = document.createElement('div');
      wrapper.className = 'ir-related-cards-carousel';
      const track = document.createElement('div');
      track.className = 'ir-related-cards-carousel__track';
      for (const item of items) {
        const slide = document.createElement('div');
        slide.className = 'ir-related-cards-carousel__slide';
        slide.appendChild(createCard(item));
        track.appendChild(slide);
      }
      wrapper.appendChild(track);

      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'ir-related-cards-carousel__prev';
      prevBtn.setAttribute('aria-label', '前へ');
      prevBtn.style.display = 'none';
      const prevSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      prevSvg.setAttribute('viewBox', '0 0 24 24');
      const prevUse = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      prevUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/common/svg/sprite.svg#arrow_left');
      prevSvg.appendChild(prevUse);
      prevBtn.appendChild(prevSvg);

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'ir-related-cards-carousel__next';
      nextBtn.setAttribute('aria-label', '次へ');
      const nextSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      nextSvg.setAttribute('viewBox', '0 0 24 24');
      const nextUse = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      nextUse.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/common/svg/sprite.svg#arrow_right');
      nextSvg.appendChild(nextUse);
      nextBtn.appendChild(nextSvg);

      wrapper.appendChild(prevBtn);
      wrapper.appendChild(nextBtn);

      currentIndex = 0;
      function update() {
        track.style.transform = `translateX(-${currentIndex * 50}%)`;
        prevBtn.style.display = currentIndex > 0 ? '' : 'none';
        nextBtn.style.display = currentIndex < items.length - slidesPerView ? '' : 'none';
      }
      prevBtn.addEventListener('click', () => { if (currentIndex > 0) { currentIndex--; update(); } });
      nextBtn.addEventListener('click', () => { if (currentIndex < items.length - slidesPerView) { currentIndex++; update(); } });
      update();

      return wrapper;
    }

    function checkResize() {
      const nowMobile = window.innerWidth <= 1024;
      if (nowMobile === isMobile) return;
      isMobile = nowMobile;
      while (el.firstChild) el.removeChild(el.firstChild);
      if (isMobile) {
        el.className = '';
        el.appendChild(buildCarousel());
      } else {
        el.className = 'ir-related-cards';
        for (const item of items) el.appendChild(createCard(item));
      }
    }

    window.addEventListener('resize', checkResize);
    checkResize();
  });
}

// ====================================================================
// News List + Pager (ページネーション)
// ====================================================================
function initNewsLists() {
  document.querySelectorAll('[data-news-list]').forEach(listEl => {
    const items = JSON.parse(listEl.dataset.newsList);
    const perPage = Number(listEl.dataset.perPage) || 10;
    const totalPages = Math.ceil(items.length / perPage);
    const pagerEl = listEl.nextElementSibling;
    if (!pagerEl || !pagerEl.hasAttribute('data-news-pager')) return;
    let currentPage = 1;

    function createSvgIcon(name, size) {
      const span = document.createElement('span');
      span.className = `icon-${size}`;
      span.setAttribute('aria-hidden', 'true');
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', `/common/svg/sprite.svg#${name}`);
      svg.appendChild(use);
      span.appendChild(svg);
      return span;
    }

    function renderItem(item) {
      const li = document.createElement('li');
      li.className = 'mod-newsList02__item rt_bn_newsList__item';
      const a = document.createElement('a');
      a.href = item.href || '#';
      a.className = 'mod-newsList02__link rt_bn_newsList__link';
      if (item.blank) { a.target = '_blank'; a.rel = 'noopener noreferrer'; }

      const time = document.createElement('time');
      time.className = 'mod-newsList02__date';
      time.textContent = item.date || '';
      a.appendChild(time);

      if (item.category) {
        const cat = document.createElement('span');
        cat.className = 'mod-newsList02__tag rt_bn_newsList__tag';
        cat.textContent = item.category;
        a.appendChild(cat);
      }

      const content = document.createElement('div');
      content.className = 'mod-newsList02__content';
      const p = document.createElement('p');
      p.className = 'mod-newsList02__title';
      const titleIn = document.createElement('span');
      titleIn.className = 'mod-newsList02__title-in';
      titleIn.textContent = item.title || '';
      p.appendChild(titleIn);

      if (item.pdf) {
        const pdfSpan = document.createElement('span');
        pdfSpan.className = 'mod-newsList02__pdf rt_bn_newsList__pdf';
        pdfSpan.appendChild(createSvgIcon('pdf', 'lg'));
        if (item.pdfSize) {
          const sizeSpan = document.createElement('span');
          sizeSpan.className = 'mod-newsList02__pdf-size';
          sizeSpan.textContent = `(${item.pdfSize})`;
          pdfSpan.appendChild(sizeSpan);
        }
        p.appendChild(pdfSpan);
      }

      if (item.blank) {
        const blankSpan = document.createElement('span');
        blankSpan.className = 'mod-newsList02__blank rt_bn_newsList__blank';
        blankSpan.appendChild(createSvgIcon('blank', 'lg'));
        p.appendChild(blankSpan);
      }

      content.appendChild(p);
      a.appendChild(content);
      li.appendChild(a);
      return li;
    }

    function updatePager() {
      while (pagerEl.firstChild) pagerEl.removeChild(pagerEl.firstChild);
      if (totalPages <= 1) return;

      function makeBtn(label, iconName, iconSize, disabled, onClick) {
        const btn = document.createElement('button');
        btn.className = 'mod-pager__btn';
        btn.setAttribute('aria-label', label);
        if (disabled) btn.disabled = true;
        btn.appendChild(createSvgIcon(iconName, iconSize));
        btn.addEventListener('click', onClick);
        return btn;
      }

      pagerEl.appendChild(makeBtn('最初のページ', 'arrow_left_double', 'sm', currentPage === 1, () => goTo(1)));
      pagerEl.appendChild(makeBtn('前のページ', 'arrow_left', 'sm', currentPage === 1, () => goTo(currentPage - 1)));

      for (let p = 1; p <= totalPages; p++) {
        const btn = document.createElement('button');
        btn.className = 'mod-pager__page' + (p === currentPage ? ' __active' : '');
        btn.setAttribute('aria-label', `${p}ページ`);
        if (p === currentPage) btn.setAttribute('aria-current', 'page');
        btn.textContent = p;
        const page = p;
        btn.addEventListener('click', () => goTo(page));
        pagerEl.appendChild(btn);
      }

      pagerEl.appendChild(makeBtn('次のページ', 'arrow_right', 'sm', currentPage === totalPages, () => goTo(currentPage + 1)));
      pagerEl.appendChild(makeBtn('最後のページ', 'arrow_right_double', 'sm', currentPage === totalPages, () => goTo(totalPages)));
    }

    function update() {
      const pageItems = items.slice((currentPage - 1) * perPage, currentPage * perPage);
      const ul = listEl.querySelector('.mod-newsList02__list');
      if (ul) {
        while (ul.firstChild) ul.removeChild(ul.firstChild);
        for (const item of pageItems) ul.appendChild(renderItem(item));
      }
      updatePager();
    }

    function goTo(page) {
      if (page < 1 || page > totalPages) return;
      currentPage = page;
      update();
    }

    // 初期 pager のイベントバインド
    update();
  });
}

// ====================================================================
// CodeBlock Copy（パーツライブラリ専用：コードブロックのコピー機能）
// ====================================================================
// .partspage-code-btn クリック時に同じラッパー内の <pre> の textContent を
// クリップボードにコピーし、ボタンに一時的なフィードバックを表示する。
// dev/build どちらでも動作するようにグローバル委任で実装。
function initCodeBlockCopy() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('.partspage-code-btn');
    if (!btn) return;
    const wrap = btn.closest('.partspage-code-container-wrap');
    if (!wrap) return;
    const pre = wrap.querySelector('pre');
    if (!pre) return;

    const text = pre.textContent || '';
    const restoreLabel = btn.dataset.originalLabel || btn.textContent;
    btn.dataset.originalLabel = restoreLabel;

    const showFeedback = (label, isError) => {
      btn.textContent = label;
      btn.classList.toggle('is-copied', !isError);
      btn.classList.toggle('is-error', !!isError);
      if (btn._copyTimer) clearTimeout(btn._copyTimer);
      btn._copyTimer = setTimeout(() => {
        btn.textContent = restoreLabel;
        btn.classList.remove('is-copied');
        btn.classList.remove('is-error');
      }, 2000);
    };

    const fallbackCopy = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        showFeedback(ok ? 'コピーしました' : 'コピー失敗', !ok);
      } catch (_err) {
        showFeedback('コピー失敗', true);
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        () => showFeedback('コピーしました', false),
        () => fallbackCopy()
      );
    } else {
      fallbackCopy();
    }
  });
}

// ====================================================================
// Scroll Hint（横スクロール可能なコンテンツにヒント表示）
// ====================================================================
// 対象セレクタ:
//   - .mod-tblWrap            : 既存のテーブル横スクロールラッパー
//   - .mod-tblWrap__sp        : SP/タブレットのみ横スクロールするラッパー（SPでガイド表示）
//   - .js-scroll-hint         : 任意の要素にヒントを付けたい場合に追加
import ScrollHint from 'scroll-hint';
// scroll-hint の updateItem / updateStatus は item.element 配下に
// .scroll-hint-icon-wrap がある前提だが、本ファイルでは外側ラッパー
// (.mod-tblWrap-outer) へ移動しているため null となり、classList 更新が
// スキップされて is-active 等が付かない。元関数呼び出しの間だけ icon-wrap
// を target 内に戻し、終了後に外側へ戻す。
(() => {
  const proto = ScrollHint && ScrollHint.prototype;
  if (!proto || proto.__nullGuardPatched) return;
  const origUpdateItem = proto.updateItem;
  const origUpdateStatus = proto.updateStatus;
  const findIconWrap = (el) => {
    const inside = el.querySelector('.scroll-hint-icon-wrap');
    if (inside) return { wrap: inside, moved: false };
    const outer = el.parentNode;
    if (outer && outer.classList && outer.classList.contains('mod-tblWrap-outer')) {
      const w = outer.querySelector(':scope > .scroll-hint-icon-wrap');
      if (w) {
        el.appendChild(w);
        return { wrap: w, moved: true, outer };
      }
    }
    return null;
  };
  proto.updateItem = function (item) {
    const el = item && item.element;
    if (!el) return;
    // ヒント消去まで完了したら以降の updateItem は完全に no-op にする。
    if (item.__hintDone) return;
    // 初回ヒント表示後の window scroll 由来の呼び出しは no-op にする。
    // scroll-hint は window scroll 毎に updateItem を呼び、その中で
    // addClass(target, 'is-active') を冪等とはいえ毎フレーム実行している。
    // iOS Safari では「スクロール中にターゲット要素のクラス操作が走り続ける」
    // ことそれ自体が ::before のアニメ描画と干渉してブレを引き起こすため、
    // ヒントが付いた後は動作を停止させる。
    // ただしユーザーがテーブルを実際に横スクロールした場合（item.interacted）は
    // ヒントを消すために実行させる（scroll-hint 本体が needSuggest=false で
    // is-active を除去する）。これを凍結すると「スクロールしてもヒントが
    // 消えない」不具合になる。
    if (item.__hintFrozen && !item.interacted) return;
    const found = findIconWrap(el);
    if (!found) return;
    try { return origUpdateItem.call(this, item); }
    finally {
      const active = found.wrap.classList && found.wrap.classList.contains('is-active');
      if (active) {
        // is-active が付いたら以降の window scroll 由来の update を凍結
        item.__hintFrozen = true;
      } else if (item.interacted) {
        // 横スクロールでヒントが消えた → 以降は完全停止（再描画も走らせない）
        item.__hintDone = true;
      }
      if (found.moved) found.outer.appendChild(found.wrap);
    }
  };
  proto.updateStatus = function (item) {
    const el = item && item.element;
    if (!el) return;
    const found = findIconWrap(el);
    if (!found) return;
    try { return origUpdateStatus.call(this, item); }
    finally { if (found.moved) found.outer.appendChild(found.wrap); }
  };
  proto.__nullGuardPatched = true;
})();


function initScrollHint() {
  const targets = document.querySelectorAll('.mod-tblWrap, .mod-tblWrap__sp, .mod-tblWrap__sticky, .js-scroll-hint');
  if (!targets.length) return;
  const lang = (document.documentElement.lang || '').toLowerCase();
  const scrollableLabel = lang.startsWith('en') ? 'Scrollable' : 'スクロールできます';

  const applyByViewport = () => {
    const isMobileOrTablet = window.innerWidth <= 1024;

    targets.forEach((target) => {
      if (isMobileOrTablet) {
        if (target.dataset.scrollHintInitialized === 'true') return;
        try {
          // 外側ラッパーを作成して target を包む（.scroll-hint-icon-wrap の格納先）
          let outerWrap = target.parentNode;
          if (!outerWrap || !outerWrap.classList.contains('mod-tblWrap-outer')) {
            outerWrap = document.createElement('div');
            outerWrap.className = 'mod-tblWrap-outer';
            target.parentNode.insertBefore(outerWrap, target);
            outerWrap.appendChild(target);
          }

          new ScrollHint(target, {
            suggestiveShadow: false,
            i18n: {
              scrollable: scrollableLabel,
            },
          });
          // ScrollHintが overflow:auto を強制するため、縦スクロールを抑止
          target.style.overflowY = 'hidden';
          target.dataset.scrollHintInitialized = 'true';

          // ScrollHint が target 内に挿入した .scroll-hint-icon-wrap を外側ラッパーへ移動
          const iconWrap = target.querySelector(':scope > .scroll-hint-icon-wrap');
          if (iconWrap) outerWrap.appendChild(iconWrap);
        } catch (e) {
          console.warn('scroll-hint init failed', e);
        }
        return;
      }

      if (target.dataset.scrollHintInitialized !== 'true') return;
      target.dataset.scrollHintInitialized = 'false';
      target.style.removeProperty('overflow-y');
      target.classList.remove('is-scrollable', 'is-left-scrollable', 'is-right-scrollable');
      // 外側ラッパー側に移動済みのアイコンも含めて削除
      const outerWrap = target.parentNode;
      if (outerWrap && outerWrap.classList.contains('mod-tblWrap-outer')) {
        outerWrap.querySelectorAll(':scope > .scroll-hint-icon-wrap').forEach((icon) => icon.remove());
      }
      target.querySelectorAll('.scroll-hint-icon-wrap').forEach((icon) => icon.remove());
    });
  };

  applyByViewport();
  window.addEventListener('resize', applyByViewport);
}
